import {SpaceTr, Tr} from "@/i18n/i18n";
import Scene = Phaser.Scene;
import {Keys} from "@/constants/key";
import TextUtils from "@/utils/text-utils";
import {ArtifactNotification, EffectType, ForceNotification} from "@/store/artifact/types";
import {SquareButton} from "@/scenes.ui/square-button";
import {Fonts} from "@/constants/font";
import {VerticalStandardPanel} from "@/scenes.ui/vertical-standard-panel";
import Container = Phaser.GameObjects.Container;
import ArtifactUtils from "@/utils/artifact-utils";
import {ImageButton} from "@/scenes.ui/image-button";
import BaseSound = Phaser.Sound.BaseSound;
import SoundUtils from "@/utils/sound-utils";
import SpriteUtils from "@/utils/sprite-utils";
import CU from "@/utils/c-u";
import SkillUtils from "@/utils/skill-utils";
import {CreatureUtils} from "@/utils/creature-utils";
import PlayerStore from "@/store/player";

export class ForceUpdatesPanel extends VerticalStandardPanel {

    private centralContainer!: Container;

    private openingSound!: BaseSound | undefined;
    private clickSound!: BaseSound | undefined;

    constructor(scene: Scene, canvas: HTMLCanvasElement, nts: ForceNotification[], openingSound: BaseSound | undefined,
                clickSound: BaseSound | undefined, container?: Container) {
        super(scene, canvas, Tr('poka.vas.ne.bilo'), container);
        const self = this;
        this.openingSound = openingSound;
        this.clickSound = clickSound;

        this.centralContainer = new Container(scene);


        let text = PlayerStore.getPlayer().lang == 'RU' ?
            Tr('zaversheno.uluchshenie') + ' ' + (nts.length > 1 ? (String(nts.length) + SpaceTr('forces.gen')) : SpaceTr('force.gen')) :
            (nts.length > 1 ?  SpaceTr('forces.gen') : SpaceTr('force.gen')) + ' ' + Tr('zaversheno.uluchshenie');
        let updateText = TextUtils.addTextByFamily(scene, text,
            0, -170, '#ffd465', 17, 'normal', 400, Fonts.MYRIADPRO_BOLD);


        let namesById = new Map<number, string>();
        for (let nt of nts) {
            let creature = PlayerStore.getPlayer().creatures.find(c => c.id == nt.creatureId);
            if(creature) {
                namesById.set(nt.creatureId, creature.name);
            }
        }

        let creatureNames = namesById.size > 0? Array.from(namesById.values()).join(', ') : "";

        let info = creatureNames + ' ' + (namesById.size > 1 ? Tr('teper.vladeut') : Tr('teper.vladeet')) +
            (nts.length > 1? Tr('novimi.darami') : Tr('novim.darom'));
        let infoText = TextUtils.addTextByFamily(scene, info,
            0, 40, '#85ff85', 15, 'normal', 450, Fonts.MYRIADPRO_BOLD);
        infoText.setVisible(!!creatureNames);


        if (nts.length === 1) {
            let nt = nts[0];

            let lightFrom = SpriteUtils.addSprite(this.scene, -90, -90, Keys.ATLAS_PROFILE, Keys.UI_ART_LIGHT, 0.8);
            let lightTo = SpriteUtils.addSprite(this.scene, 90, -90, Keys.ATLAS_PROFILE, Keys.UI_ART_LIGHT, 0.8);
            let itemFrom = SpriteUtils.addSprite(this.scene, -90, -90,
                CreatureUtils.getForceAtlasLarge(nt.warriorClass), CreatureUtils.getLargeForceIcon(nt.warriorClass, true));
            let fromText = TextUtils.addTextAdvanced(scene, Tr('rang') + " " + nt.prevLevel, -90, -20,
                '#e0e0e0', 15, 'normal', 150, 'center', 2, Fonts.ARIAL_MT);
            let itemArr = SpriteUtils.addSprite(this.scene, 0, -90, Keys.ATLAS_PROFILE, Keys.UI_ART_ARR);
            let itemTo = SpriteUtils.addSprite(this.scene, 90, -90,
                CreatureUtils.getForceAtlasLarge(nt.warriorClass), CreatureUtils.getLargeForceIcon(nt.warriorClass, true));
            let toText = TextUtils.addTextAdvanced(scene, Tr('rang') + " " + nt.nextLevel, 90, -20,
                '#85ff85', 15, 'normal', 150, 'center', 2, Fonts.ARIAL_MT);
            this.add([lightFrom, lightTo, itemFrom, itemTo, itemArr, fromText, toText]);

        } else if (nts.length <= 4) {
            let cellWidth = 110;
            let xPos = cellWidth / 2 - nts.length * cellWidth / 2;
            let yPos = -90;
            for (let nt of nts) {
                let itemImage = SpriteUtils.addSprite(this.scene, xPos, yPos,
                    CreatureUtils.getForceAtlasLarge(nt.warriorClass), CreatureUtils.getLargeForceIcon(nt.warriorClass, true));
                SpriteUtils.setDispSize(itemImage, cellWidth / 1.1 * CU.S, cellWidth / 1.1 * CU.S);
                let effectText = TextUtils.addTextAdvanced(scene, Tr('rang') + " " + nt.nextLevel, xPos, -17,
                    '#e5e5e5', 12, 'normal', 110, 'center', 2, Fonts.ARIAL_MT);
                this.add([itemImage, effectText]);
                xPos += cellWidth;
            }
        } else if (nts.length > 4) {
            this.initNavPanel(scene, nts, 0);
        }

        let okButton = new SquareButton(this.scene, 0, 155, 0, Keys.UI_ART_GREEN_BUTTON,
            Keys.ATLAS_PROFILE, Keys.UI_GOLDEN_CHECK, Tr('otlichno').toUpperCase(), false);

        okButton.setCallback(() => {
            self.close();
            SoundUtils.play(self.clickSound);
        });

        this.add([updateText, okButton, infoText]);
    }

    public open(): void {
        super.open();
        SoundUtils.play(this.openingSound);
    }

    private initNavPanel(scene: Scene, nts: ForceNotification[], startIndex: number) {
        this.centralContainer.setVisible(false);
        this.centralContainer.destroy();
        this.centralContainer = new Container(scene);
        this.add(this.centralContainer);
        let sublist = []
        while (startIndex < 0) {
            startIndex += nts.length;
        }
        sublist.push(nts[startIndex % nts.length]);
        sublist.push(nts[(startIndex + 1) % nts.length]);
        sublist.push(nts[(startIndex + 2) % nts.length]);

        let cellWidth = 110;
        let xPos = -120;
        let yPos = -90;
        for (let nt of sublist) {
            let itemImage = SpriteUtils.addSprite(this.scene, xPos, yPos,
                CreatureUtils.getForceAtlasLarge(nt.warriorClass), CreatureUtils.getLargeForceIcon(nt.warriorClass, true));
            SpriteUtils.setDispSize(itemImage, cellWidth / 1.1 * CU.S, cellWidth / 1.1 * CU.S);
            let effectText = TextUtils.addTextAdvanced(scene, Tr('rang') + " " + nt.nextLevel, xPos, -17,
                '#e5e5e5', 12, 'normal', 110, 'center', 2, Fonts.ARIAL_MT);
            this.centralContainer.add([itemImage, effectText]);
            xPos += cellWidth;
        }

        const leftButton = new ImageButton(this.scene, -190, -90, Keys.ATLAS_PROFILE, Keys.UI_ARROW_LEFT);
        const rightButton = new ImageButton(this.scene, 190, -90, Keys.ATLAS_PROFILE, Keys.UI_ARROW_RIGHT);
        this.add(leftButton);
        this.add(rightButton);

        leftButton.setCallback(() => {
            this.initNavPanel(scene, nts, startIndex - 1);
        });
        rightButton.setCallback(() => {
            this.initNavPanel(scene, nts, startIndex + 1);
        });
    }
}
